import { S3Client, S3ClientConfig } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import type { AwsCredentialIdentityProvider } from "@aws-sdk/types";

import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import { APIService } from "@/shared/services/ApiService";
import { LoggerService } from "@/shared/services/LoggerService";
import {
  APIResponse,
  datasetsManifestTransactionBeginEndpoint,
  datasetsManifestTransactionCompleteEndpoint,
  filesUploadBeginEndpoint,
  filesUploadCompleteEndpoint,
} from "@/types";

import { UploadableFile } from "./UploadableFile";

export function fileSystemEntryIsFile(
  entry: FileSystemEntry,
): entry is FileSystemFileEntry {
  return entry.isFile;
}

export function fileSystemEntryIsDirectory(
  entry: FileSystemEntry,
): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

/**
 * Iteratively yield FileSystemEntries from a directory.
 *
 * Slightly modified from a sample in the W3C File and Directory Entries API spec.
 */
export async function* getFileEntries(
  dirEntry: FileSystemDirectoryEntry,
): AsyncGenerator<FileSystemFileEntry> {
  const reader = dirEntry.createReader();
  const getNextBatch = () => {
    return new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
  };

  let entries;
  do {
    entries = await getNextBatch();
    for (const entry of entries) {
      if (fileSystemEntryIsFile(entry)) {
        yield entry;
      } else if (fileSystemEntryIsDirectory(entry)) {
        for await (const nestedEntry of getFileEntries(entry)) {
          yield nestedEntry;
        }
      } else {
        throw new Error("Impossible code path");
      }
    }
  } while (entries.length > 0);
}

export function fileFromFileSystemFileEntry(
  fileEntry: FileSystemFileEntry,
  uploadItemKey?: string,
): Promise<UploadableFile> {
  return new Promise<UploadableFile>((resolve, reject) => {
    fileEntry.file((file: File) => {
      let relativePath = file.webkitRelativePath;
      if (relativePath === "") {
        if (fileEntry.fullPath.startsWith("/")) {
          relativePath = fileEntry.fullPath.slice(1);
        } else {
          relativePath = fileEntry.fullPath;
        }
      }
      resolve(
        new UploadableFile(
          file,
          relativePath,
          uploadItemKey ? uploadItemKey : fileEntry.fullPath,
        ),
      );
    }, reject);
  });
}

/**
 * Constructs the S3 client an upload runs through. Injectable so tests can drive the retry and
 * credential-refresh paths, which are otherwise only reachable against a real bucket.
 */
export type S3ClientFactory = (config: S3ClientConfig) => S3Client;

export const uploadManifestFile = async (
  file: UploadableFile,
  destUri: string,
  trackUploadProgress: (arg0: number) => void,
  credentialsProvider: AwsCredentialIdentityProvider,
  region: string,
  { signal }: { signal?: AbortSignal },
  makeClient: S3ClientFactory = (config) => new S3Client(config),
): Promise<void> => {
  const s3Client = makeClient({
    region,
    credentials: credentialsProvider,
  });

  let keyUri = destUri;
  if (destUri.startsWith("s3://")) {
    keyUri = destUri.slice("s3://".length);
  }

  const keyParts = keyUri.split("/");
  const bucket = keyParts.shift();
  const key = keyParts.join("/");

  // https://docs.aws.amazon.com/AWSJavaScriptSDK/v3/latest/Package/-aws-sdk-lib-storage/Class/Upload/
  const upload = new Upload({
    client: s3Client,
    params: {
      Bucket: bucket,
      Key: key,
      Body: file.file,
    },
  });

  let prevLoaded = 0;
  upload.on("httpUploadProgress", (progress) => {
    const loaded = progress.loaded ?? 0;
    const chunkSize = loaded - prevLoaded;
    prevLoaded = loaded;
    trackUploadProgress(chunkSize);

    if (signal?.aborted) {
      void upload.abort();
    }
  });

  try {
    await upload.done();
  } catch (e) {
    if (e instanceof Error) {
      if (e.name === "AbortError") {
        return;
      }

      // This function resolves identically whether the upload succeeded or failed, so the caller
      // carries on and still completes the transaction -- which marks a file available that was
      // never written. Fixing that means uploading the rest of the batch and reporting the failures
      // (ENG-2988); until then, log so the failure rate is at least measurable.
      LoggerService.error("Upload failed silently", e);
    } else {
      throw e;
    }
  }
};

// File path -> file size in bytes
type ResourceManifest = { [key: string]: number };

/**
 * Begins an upload of the files in `resourceManifest` to `associationId`.
 *
 * A dataset's upload goes through the dataset batch upload route. An org's,
 * device's or device type's goes through `POST /v1/files/upload`, which the
 * server refuses while the org's `release-asset-store` flag is off.
 */
export const beginManifestTransaction = async (
  associationId: string,
  resourceManifest: ResourceManifest,
  orgId: string,
): Promise<{
  transaction_id: string | null;
  uploadMappings: { [key: string]: string } | null;
  error: Error | null;
}> => {
  const associationType = fileAssociationType(associationId);
  const { response, error } = await APIService.authorizedRequest<
    APIResponse<{
      transaction_id: string;
      upload_mappings: { [key: string]: string };
    }>
  >(
    associationType === AssociationType.Dataset
      ? {
          method: "POST",
          endpoint: datasetsManifestTransactionBeginEndpoint,
          apiVersion: "v2",
          requestBody: JSON.stringify({
            resource_manifest: resourceManifest,
            origination: "Roboto Web App",
          }),
          pathParams: {
            datasetId: associationId,
          },
          orgId,
        }
      : {
          method: "POST",
          endpoint: filesUploadBeginEndpoint,
          requestBody: JSON.stringify({
            association: {
              association_id: associationId,
              association_type: associationType,
            },
            resource_manifest: resourceManifest,
            origination: "Roboto Web App",
          }),
          orgId,
        },
  );

  if (error) {
    LoggerService.error("Error beginning transaction", error);
    return {
      transaction_id: null,
      uploadMappings: null,
      error,
    };
  }

  if (response?.data === undefined) {
    const error = new Error("No data returned from transaction endpoint");
    LoggerService.error("No data returned from transaction endpoint", error);
    return {
      transaction_id: null,
      uploadMappings: null,
      error,
    };
  }

  return {
    transaction_id: response.data.transaction_id,
    uploadMappings: response.data.upload_mappings,
    error,
  };
};

export const completeManifestFileUpload = async (
  associationId: string,
  orgId: string,
  uploadId: string,
) => {
  const { error: errResp } = await APIService.authorizedRequest(
    fileAssociationType(associationId) === AssociationType.Dataset
      ? {
          method: "PUT",
          endpoint: datasetsManifestTransactionCompleteEndpoint,
          apiVersion: "v2",
          orgId: orgId,
          pathParams: {
            datasetId: associationId,
            uploadId,
          },
        }
      : {
          method: "PUT",
          endpoint: filesUploadCompleteEndpoint,
          orgId,
          pathParams: { transactionId: uploadId },
        },
  );

  if (errResp) {
    throw errResp;
  }
};
